import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { useDispatch } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { escrow, onboarding } from '@portal/config/app.json';
import { Alert, Col, Row } from 'antd';

import { CompanyType } from '@portal/models/enumerators';

import { AuthActions, OnboardingActions } from '@portal/actions';
import { translate } from '@portal/services/i18n';
import * as MessageService from '@portal/services/message';
import {
  maskCNAE,
  maskCnpj,
  maskMoney,
  maskPhoneInput,
  unmaskField,
} from '@portal/services/masks';

import AdvancedDateTimePicker from '@portal/components/AdvancedDateTimePicker/AdvancedDateTimePicker';
import AdvancedAutocomplete from '@portal/components/AdvancedAutocomplete/AdvancedAutocomplete';
import AdvancedCheckbox from '@portal/components/AdvancedCheckbox/AdvancedCheckbox';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import useReduxState from '@portal/hooks/useReduxState';
import { useNavigation } from '@portal/hooks/useNavigation';
import MessageModal from '../MessageModal/MessageModal';
import {
  ONBOARDING_MAXLENGTH_EMAIL,
  ONBOARDING_MAXLENGTH_LEGALNAME,
  VITE_APPLICATION_NAME,
} from '@portal/config/env';
import StorageService from '@portal/services/storage';
import ThemedText from '@portal/components/ThemedText/ThemedText';

interface IProps {
  userData: models.OnboardingRequest;
  onFormChange: (key: string, value: unknown) => void;
  companyTypeValue: Array<{ value: CompanyType; name: string }>;
  businessLineValue: Array<{ value: number; name: string }>;
  setIsValidDate: (value: boolean) => void;
  handleFirstStepCompany: (value: models.CreateAccount) => void;
}

const CreateAccountCorporative = ({
  companyTypeValue,
  businessLineValue,
  setIsValidDate,
  handleFirstStepCompany,
  userData,
}: IProps) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [taxIdentifier, setTaxIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [bornAt, setBornAt] = useState('');
  const [fantasyName, setFantasyName] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [nire, setNire] = useState('');
  const [businessLine, setBusinessLine] = useState<number>();
  const [companyType, setCompanyType] = useState<number>();
  const [cnae, setCnae] = useState('');
  const [businessRegistration, setBusinessRegistration] = useState('');
  const [financialStatistic, setFinancialStatistic] = useState<number>();
  const [income, setIncome] = useState(0);
  const [maskedIncome, setMaskedIncome] = useState('');
  const [noBusinessRegistrationFlag, setNoBusinessRegistrationFlag] =
    useState(false);
  const [isEscrowAccount, setIsEscrowAccount] = useState(false);
  const [isEscrowModalVisible, setEscrowModalVisible] = useState(false);

  const {
    onboarding: { settings, cnaeDescription },
  } = useReduxState();

  const navigate = useNavigation<{
    isRetry?: boolean;
  }>();

  const handleChangedBirthday = (date: MaterialUiPickersDate) => {
    if (date) {
      const valid =
        date <= DateTime.now() &&
        date >= DateTime.fromJSDate(new Date(1899, 11, 31));
      if (valid) {
        setIsValidDate(true);
      } else {
        setIsValidDate(false);
      }
    }
  };

  const onIncomeChange = (incomeValue: string) => {
    const letters = /^[a-zA-Z]*$/;

    if (incomeValue.length > 22) {
      return;
    }

    if (letters.test(incomeValue)) {
      return;
    }

    const maskedValue = maskMoney(incomeValue);
    setMaskedIncome(maskedValue);

    const formattedValue = parseFloat(
      maskedValue.substring(2).split('.').join('').replace(/,/g, '.')
    );

    setIncome(formattedValue);
  };

  const handleLogout = () => {
    StorageService.removeItem('session-token');
    dispatch(AuthActions.logout());
    navigate('/');
  };

  useEffect(() => {
    if (navigate?.location?.state?.isRetry && userData?.account) {
      if (userData.account.name) {
        setName(userData.account.name);
      }
      if (userData.account.taxIdentifier) {
        setTaxIdentifier(userData.account.taxIdentifier);
      }
      if (userData.account.email) {
        setEmail(userData.account.email);
      }
      if (userData.account.bornAt) {
        setBornAt(userData.account.bornAt);
      }
      if (userData.account.fantasyName) {
        setFantasyName(userData.account.fantasyName);
      }
      if (userData.account.cellphone) {
        setCellphone(userData.account.cellphone);
      }
      if (userData.account.nire) {
        setNire(userData.account.nire);
      }
      if (userData.account.businessLine) {
        setBusinessLine(userData.account.businessLine);
      }
      if (userData.account.companyType) {
        setCompanyType(userData.account.companyType);
      }
      if (userData.account.cnae) {
        setCnae(userData.account.cnae);
      }
      if (userData.account.businessRegistration) {
        setBusinessRegistration(userData.account.businessRegistration);
      }
      if (userData.account.financialStatistic) {
        setFinancialStatistic(userData.account.financialStatistic);
      }
      if (userData.account.income) {
        const incomeValue = userData.account.income;
        setIncome(incomeValue);
        const incomeString =
          typeof incomeValue === 'number'
            ? incomeValue.toString()
            : (incomeValue as string);
        const maskedValue = maskMoney(incomeString);
        setMaskedIncome(maskedValue);
      }
    }
  }, [navigate?.location?.state?.isRetry, userData]);

  const handleCnaeCheckbox = (flag: boolean) => {
    if (flag) {
      setNoBusinessRegistrationFlag(true);
      setBusinessRegistration('');
    } else {
      setNoBusinessRegistrationFlag(false);
    }
  };

  const renderEscrowCheckModal = () =>
    escrow.enabled && (
      <MessageModal
        open={isEscrowModalVisible}
        onCancel={() => setEscrowModalVisible(false)}
        setVisible={setEscrowModalVisible}
        buttonOkText={translate(
          'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.ESCROW.MODAL.CONFIRM'
        )}
        buttonCancelText={translate(
          'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.ESCROW.MODAL.CANCEL'
        )}
        title={translate(
          'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.ESCROW.MODAL.TITLE'
        )}
        onConfirm={() => {
          setIsEscrowAccount(true);
          setEscrowModalVisible(false);
        }}
        message={`
          ${translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.ESCROW.MODAL.BODY_START'
          )} ${VITE_APPLICATION_NAME} ${translate(
          'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.ESCROW.MODAL.BODY_END'
        )}`}
      />
    );

  return (
    <div className="create-account-user">
      <AdvancedForm>
        <Row gutter={16} className="create-account-user__col-margin-top">
          {!navigate?.location?.state?.isRetry && (
            <Col
              className="create-account-user__col create-account-user__col"
              span={8}
            >
              <AdvancedInput
                variant="outlined"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.TYPE_ACCOUNT.CNPJ'
                )}
                value={maskCnpj(taxIdentifier)}
                onChange={(value) => {
                  setTaxIdentifier(unmaskField(value));
                }}
              />
            </Col>
          )}
          <Col
            className="create-account-user__col create-account-user__col"
            span={8}
          >
            <AdvancedInput
              helperText
              maxLength={
                onboarding.inputMaxLength
                  ? ONBOARDING_MAXLENGTH_LEGALNAME
                  : undefined
              }
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.COMPANY_NAME.LABEL'
              )}
              value={name}
              variant="outlined"
              onChange={(value) => {
                setName(value.replace(/[^a-zA-Z0-9 &/`'-]/g, '').toUpperCase());
              }}
            />
            <ThemedText variant="note" color="gray">
              {translate('PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.NAME_WARN')}
            </ThemedText>
          </Col>
          <Col
            className="create-account-user__col create-account-user__col"
            span={8}
          >
            <AdvancedInput
              variant="outlined"
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.FANTASY_NAME'
              )}
              value={fantasyName}
              onChange={(value) => setFantasyName(value)}
            />
            <ThemedText variant="note" color="gray">
              {translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FANTASY_NAME_WARN'
              )}
            </ThemedText>
          </Col>
        </Row>
        <Row gutter={16} className="create-account-user__col">
          {onboarding.allowNire ? (
            <Col
              className="create-account-user__col create-account-user__col"
              span={12}
            >
              <AdvancedInput
                variant="outlined"
                className="create-account-user__inputLabel"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.NIRE.TITLE'
                )}
                value={nire}
                onChange={(value) => setNire(unmaskField(value))}
                maxLength={11}
              />
              <p className="create-account-user__text-warn">
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.NIRE.SUB_TITLE'
                )}
              </p>
            </Col>
          ) : null}
          {!navigate?.location?.state?.isRetry && (
            <Col
              className="create-account-user__col create-account-user__col"
              span={12}
            >
              <AdvancedInput
                helperText
                maxLength={
                  onboarding.inputMaxLength
                    ? ONBOARDING_MAXLENGTH_EMAIL
                    : undefined
                }
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.EMAIL_CORPORATIVE'
                )}
                value={email}
                variant="outlined"
                onChange={(value) => {
                  setEmail(value);
                }}
              />
            </Col>
          )}
          {!navigate?.location?.state?.isRetry && (
            <Col
              className="create-account-user__col create-account-user__col"
              span={12}
            >
              <AdvancedInput
                variant="outlined"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.CELLPHONE'
                )}
                value={maskPhoneInput(cellphone)}
                onChange={(value) => {
                  if (unmaskField(value).length <= 11) {
                    setCellphone(unmaskField(value));
                  }
                }}
              />
            </Col>
          )}
        </Row>
        <Row gutter={16} className="create-account-user__col">
          <Col
            className="create-account-user__col create-account-user__col"
            span={12}
          >
            <AdvancedInput
              variant="outlined"
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.CORPORATIVE.INCOME'
              )}
              value={maskedIncome}
              onChange={onIncomeChange}
            />
          </Col>
          <Col
            className="create-account-user__col create-account-user__col"
            span={12}
          >
            <AdvancedDateTimePicker
              onlyDate
              variant="outlined"
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.CORPORATIVE.OPEN'
              )}
              disableFuture
              value={bornAt}
              onChange={(value) => {
                handleChangedBirthday(value);
                if (value && value.toISO()) {
                  setBornAt(value.toISODate());
                  setIsValidDate(true);
                } else {
                  setIsValidDate(false);
                }
              }}
            />
          </Col>
        </Row>
        <Row gutter={16} className="create-account-user__col">
          {onboarding.allowCompanyBranch ? (
            <Col className="create-account-user__col create-account-user__col">
              <AdvancedAutocomplete
                variant="outlined"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.CORPORATIVE.BRANCH'
                )}
                onChange={(value) => {
                  if (typeof value === 'number') {
                    setBusinessLine(value);
                  }
                }}
                options={
                  onboarding.moreOptions
                    ? settings?.businessLineMP
                        .sort((a, b) =>
                          a.description.toLowerCase() >
                          b.description.toLowerCase()
                            ? 1
                            : b.description.toLowerCase() >
                              a.description.toLowerCase()
                            ? -1
                            : 0
                        )
                        .filter((lineMP) => lineMP.type === 'PJ')
                        .map((line) => {
                          return { name: line.description, value: line.value };
                        })
                    : onboarding.allowFirstOption
                    ? (() => {
                        const sortedBusinessLineValue = [...businessLineValue];
                        sortedBusinessLineValue.sort((a, b) => {
                          if (a.value === 47) return -1;
                          if (b.value === 47) return 1;

                          return a.name
                            .toLowerCase()
                            .localeCompare(b.name.toLowerCase());
                        });
                        return sortedBusinessLineValue;
                      })()
                    : businessLineValue.sort((a, b) =>
                        a.name.toLowerCase() > b.name.toLowerCase()
                          ? 1
                          : b.name.toLowerCase() > a.name.toLowerCase()
                          ? -1
                          : 0
                      )
                }
              />
            </Col>
          ) : null}
        </Row>
        <Row gutter={16} className="create-account-user__col-margin-top">
          {onboarding.allowCompanyType ? (
            <Col
              className="create-account-user__col create-account-user__col"
              span={12}
            >
              <AdvancedAutocomplete
                variant="outlined"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.COMPANY_TYPE'
                )}
                onChange={(value) => {
                  if (typeof value === 'number') {
                    setCompanyType(value);
                  }
                }}
                options={companyTypeValue}
              />
            </Col>
          ) : null}
          {onboarding.allowCnae ? (
            <Col
              className="create-account-user__col create-account-user__col"
              span={12}
            >
              <AdvancedInput
                variant="outlined"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.CNAE'
                )}
                value={maskCNAE(cnae)}
                onChange={(value) => {
                  if (value.length >= 6 && onboarding.cnaeValidation) {
                    dispatch(
                      OnboardingActions.validateCNAE(unmaskField(value))
                    );
                  } else {
                    dispatch(OnboardingActions.cleanCNAEDescription());
                  }
                  setCnae(unmaskField(value));
                }}
              />
              {onboarding.cnaeValidation ? <h4>{cnaeDescription}</h4> : null}
            </Col>
          ) : null}
        </Row>
        {onboarding.allowBusinessRegistration ? (
          <Row
            className="create-account-user__rows create-account-user__col-margin-top"
            gutter={16}
          >
            <Col
              className="create-account-user__col create-account-user__col"
              span={12}
            >
              <AdvancedInput
                variant="outlined"
                disabled={noBusinessRegistrationFlag}
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.BUSINESS_REGISTRATION'
                )}
                value={businessRegistration}
                onChange={(value) => {
                  if (/^[0-9.]+$/g.test(value) || value.length === 0) {
                    setBusinessRegistration(value);
                  }
                }}
              />
            </Col>
            <Col className="create-account-user__col-margin-top create-account-user__full">
              <AdvancedCheckbox
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.NO_BUSINESS_REGISTRATION'
                )}
                value={noBusinessRegistrationFlag}
                onChange={(value) => {
                  handleCnaeCheckbox(value);
                }}
              />
            </Col>
          </Row>
        ) : null}
        {onboarding.moreOptions ? (
          <Row className="create-account-user__rows create-account-user__col create-account-user__col-margin-top">
            <Col className="create-account-user__col-margin-top create-account-user__full">
              <AdvancedAutocomplete
                variant="standard"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.FINANCIAL_STATISTICS'
                )}
                onChange={(value) => {
                  if (typeof value === 'number') {
                    setFinancialStatistic(value);
                  }
                }}
                options={
                  settings?.financialStatistic.map((line) => {
                    return { name: line.description, value: line.value };
                  }) ?? []
                }
              />
            </Col>
          </Row>
        ) : null}
        <Alert
          className="create-account-user__col-margin-top create-account-user__text"
          type="warning"
          message={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FOOTER'
          )}
          showIcon
        />
        <Row className="advance_final">
          {onboarding.enableLogoutOnboardingButton ? (
            <AdvancedButton
              type="button"
              text={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.ANOTHER_ACCOUNT'
              )}
              variant="outlined"
              onClick={handleLogout}
            />
          ) : null}
          <div className="advance_final__button">
            <AdvancedButton
              text={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
              )}
              type="button"
              onClick={() => {
                if (navigate?.location?.state?.isRetry) {
                  if (!name || !name.trim()) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.ALL_FIELDS'
                    );
                  }

                  if (!bornAt) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.BIRTH_DATE'
                    );
                  }

                  if (onboarding.requireFantasyName && !fantasyName) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.ALL_FIELDS'
                    );
                  }

                  if (onboarding.allowCompanyType && !companyType) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PJ.ERRORS.COMPANY_TYPE_EMPTY'
                    );
                  }

                  if (onboarding.allowCnae && !cnae) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.ALL_FIELDS'
                    );
                  }

                  if (onboarding.allowCompanyBranch && !businessLine) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.COMPANY_BRANCH'
                    );
                  }
                }

                handleFirstStepCompany({
                  isEscrow: escrow.onboarding ? isEscrowAccount : null,
                  name,
                  fantasyName,
                  taxIdentifier,
                  email,
                  bornAt,
                  businessLine,
                  companyType,
                  cellphone,
                  cnae,
                  income,
                  nire,
                  financialStatistic,
                  businessRegistration:
                    noBusinessRegistrationFlag ||
                    !onboarding.allowBusinessRegistration
                      ? unmaskField(businessRegistration)
                      : '',
                });
              }}
            />
          </div>
        </Row>
      </AdvancedForm>
      {renderEscrowCheckModal()}
    </div>
  );
};

export default CreateAccountCorporative;
